import React, { useCallback, useState } from 'react';
import { INK } from 'Utils/styleTokens';

/**
 * The trailing window every landscape stat panel and activity chart reports over.
 *
 * It used to be hard-coded at 30 days in each caller, which made the panels on one page disagree
 * with each other ("Runs — last 30 days" next to an all-time findings total). One shared window
 * keeps them consistent, and the picker below is the advanced option for changing it: it is a
 * per-user display preference, so it lives in localStorage rather than in the install-wide
 * advanced config (that would need a new platform-api field and a migration).
 */
export const DEFAULT_ACTIVITY_DAYS = 45;

export const ACTIVITY_DAY_OPTIONS = [7, 14, 30, 45, 60, 90, 180, 365];

const STORAGE_KEY = 'c2pp.activityWindowDays';

const MIN_DAYS = 1;
const MAX_DAYS = 730;

const clampDays = (value) => {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return DEFAULT_ACTIVITY_DAYS;
  return Math.min(MAX_DAYS, Math.max(MIN_DAYS, n));
};

/** Stored preference, or the default. Storage can throw (private mode, blocked site data). */
export function readActivityWindowDays() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw == null ? DEFAULT_ACTIVITY_DAYS : clampDays(raw);
  } catch (e) {
    return DEFAULT_ACTIVITY_DAYS;
  }
}

/**
 * `[days, setDays]` for the shared window. Persisted, so a user who works in 90-day windows gets
 * them on every landscape page. Not reactive across pages within one session — each page reads
 * the stored value when it mounts, which is all the landscape pages need.
 */
export function useActivityWindowDays() {
  const [days, setDays] = useState(readActivityWindowDays);
  const update = useCallback((value) => {
    const next = clampDays(value);
    setDays(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, String(next));
    } catch (e) {
      // A window that can't be remembered still works for this visit.
    }
  }, []);
  return [days, update];
}

/** "last N days" suffix for a panel label, so every caller words it the same way. */
export const windowLabel = (days) => `last ${days} days`;

/**
 * The advanced option itself: a small select beside the stat panels. Deliberately a fixed option
 * list — a free-text day count invites "0" and "1e9" for no real gain.
 */
export function ActivityWindowPicker({ days, onChange, label = 'Window' }) {
  return (
    <label
      className='sg-small d-inline-flex align-items-center'
      style={{ gap: 6, margin: 0, color: INK[500], whiteSpace: 'nowrap' }}
    >
      {label}
      <select
        value={days}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label='Reporting window in days'
        data-testid='activity-window-days'
        style={{
          font: '400 12px var(--font-ui)',
          color: 'var(--ink-700)',
          background: 'var(--surface)',
          border: `1px solid ${INK[150]}`,
          borderRadius: 'var(--r-sm)',
          padding: '2px 6px',
        }}
      >
        {ACTIVITY_DAY_OPTIONS.map((d) => (
          <option key={d} value={d}>{`${d} days`}</option>
        ))}
      </select>
    </label>
  );
}

export default ActivityWindowPicker;
